import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation, Outlet } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  LayoutDashboard, 
  Package, 
  Calendar, 
  Mail, 
  Star,
  Camera,
  FileText,
  LogOut,
  Menu,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AdminLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);

  useEffect(() => {
    verifyAuth();
  }, []);

  const verifyAuth = async () => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (error) {
      console.error('Auth verification failed:', error);
      localStorage.removeItem('adminToken');
      navigate('/admin/login');
      toast.error('Session expired. Please login again.');
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
    toast.success('Logged out successfully');
  };

  const menuItems = [
    { path: '/admin/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/admin/packages', icon: Package, label: 'Packages' },
    { path: '/admin/bookings', icon: Calendar, label: 'Bookings' },
    { path: '/admin/contacts', icon: Mail, label: 'Contacts' },
    { path: '/admin/testimonials', icon: Star, label: 'Testimonials' },
    { path: '/admin/gallery', icon: Camera, label: 'Safari Library' },
    { path: '/admin/site-content', icon: FileText, label: 'Site Content' },
  ];

  return (
    <div className="min-h-screen bg-sand">
      {/* Top Bar */}
      <div className="bg-navy text-sand border-b border-gold/20 sticky top-0 z-40">
        <div className="flex items-center justify-between px-4 py-4">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              className="lg:hidden text-sand hover:text-gold"
              data-testid="mobile-menu-toggle"
            >
              {isSidebarOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
            <h1 className="font-heading text-2xl font-light text-gold">JMD WILDLIFE Admin</h1>
          </div>
          <button
            onClick={handleLogout}
            data-testid="logout-btn"
            className="flex items-center space-x-2 hover:text-gold transition-colors"
          >
            <LogOut size={20} />
            <span className="font-body text-sm">Logout</span>
          </button>
        </div>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside
          className={`${
            isSidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } lg:translate-x-0 fixed lg:static inset-y-0 left-0 z-30 w-64 bg-white border-r border-border transition-transform duration-300 mt-[73px] lg:mt-0`}
        >
          <nav className="p-4 space-y-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setIsSidebarOpen(false)}
                  className={`flex items-center space-x-3 px-4 py-3 rounded-lg font-body transition-colors ${
                    isActive
                      ? 'bg-navy text-gold font-semibold'
                      : 'text-navy hover:bg-muted'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <Icon size={20} />
                  <span>{item.label}</span>
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="flex-1 p-6">
          <Outlet />
        </main>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-20 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
